import type { ComponentProps } from 'react';
import { useCallback, useContext, useId, useLayoutEffect, useMemo, useRef } from 'react';
import { RectClipPath } from '@visx/clip-path';
import { DataContext, XYChart } from '@visx/xychart';
import { clsx } from 'clsx';

import { useChartContext } from '../../../components/ChartContext';
import { EmptyChart } from '../../../components/EmptyChart';
import { useTooltipContext } from '../../../components/Tooltip';
import { buildLaunchPadChartTheme } from '../../utils/chartTheme';
import { normalizeDomain } from '../../utils/domainNormalizers';
import { SkeletonChart } from '../SkeletonChart';

import { DragToZoomHandler } from './DragToZoomHandler';
import { HoverTrackingHandler } from './HoverTrackingHandler';
import { ResetZoomButton } from './ResetZoomButton';

import styles from './ChartCanvas.module.css';

export interface ChartCanvasProps extends Omit<ComponentProps<typeof XYChart>, 'theme'> {
  children: React.ReactNode;
}

/**
 * Internal component that renders the clip path using actual dimensions from visx DataContext.
 * Must be rendered inside XYChart to access the DataContext.
 */
function ChartClipPath({
  clipPathId,
  margin,
}: {
  clipPathId: string;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
}) {
  const dataContext = useContext(DataContext);

  // Get actual dimensions from visx DataContext (set by XYChart internally)
  const width = dataContext?.width ?? 0;
  const height = dataContext?.height ?? 0;

  // Calculate inner dimensions (chart data area excluding margins)
  const innerWidth = width - (margin?.left ?? 0) - (margin?.right ?? 0);
  const innerHeight = height - (margin?.top ?? 0) - (margin?.bottom ?? 0);

  return (
    <RectClipPath
      id={clipPathId}
      x={margin?.left ?? 0}
      y={margin?.top ?? 0}
      width={Math.max(0, innerWidth)}
      height={Math.max(0, innerHeight)}
    />
  );
}

/**
 * Renders the XYChart with zoom-aware scale configuration.
 * Must be used inside ChartProvider.
 *
 * @example
 * ```tsx
 * <ChartProvider chartType="line">
 *   <ChartCanvas height={400} width={800}>
 *     <XAxis dataKey="timestamp" />
 *     <Series data={data} />
 *   </ChartCanvas>
 * </ChartProvider>
 * ```
 */
export function ChartCanvas({ children, ...xyChartProps }: ChartCanvasProps) {
  const {
    xScaleType,
    zoomDomain,
    chartData,
    disableDragToZoom,
    xDataKey,
    isLoading,
    onDataPointHover,
    emptyStateTitle,
    emptyStateSubtitle,
    chartClipPathId,
    yScaleConfig: contextYScaleConfig,
  } = useChartContext();
  const { setIsDirectlyHovered } = useTooltipContext();
  const containerId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  const markDirectlyHovered = useCallback(() => setIsDirectlyHovered(true), [setIsDirectlyHovered]);
  const clearDirectlyHovered = useCallback(() => setIsDirectlyHovered(false), [setIsDirectlyHovered]);

  // pointerenter alone misses the case where the chart mounts under an already-present
  // cursor (e.g. expanding a dashboard graph). pointermove recovers on any in-chart move;
  // the :hover sync below covers a still cursor after mount / loading→ready transitions.
  const pointerEventHandlers = {
    onPointerEnter: markDirectlyHovered,
    onPointerMove: markDirectlyHovered,
    onPointerLeave: clearDirectlyHovered,
  };

  // Check if chart has data to display
  const hasData = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return false;
    }

    // Check if there's at least one non-null value across all data points
    // Note: 0 is a valid data value (e.g., 0% error rate, 0 tokens) - only null/undefined indicate missing data
    const hasNonNullData = chartData.some((datum) => {
      // Check all properties except the x-axis key
      return Object.entries(datum).some(([key, value]) => {
        // Skip the x-axis key (convert both to string for comparison since xDataKey might be Symbol)
        if (String(key) === String(xDataKey)) {
          return false;
        }
        // Check if this value exists (not null/undefined) - 0 is a valid value
        return value !== null && value !== undefined;
      });
    });

    return hasNonNullData;
  }, [chartData, xDataKey]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    try {
      if (el.matches(':hover')) {
        setIsDirectlyHovered(true);
      }
    } catch {
      // Some environments don't support :hover matching; pointermove still recovers.
    }
  }, [hasData, isLoading, setIsDirectlyHovered]);

  // Build theme for LaunchPad styling
  const theme = useMemo(() => buildLaunchPadChartTheme(), []);

  // Prepare xScale config with domain (from zoom or data) for proper scale rendering
  const xScaleConfig = useMemo(() => {
    if (!hasData || !chartData) {
      return undefined;
    }

    // Determine scale type and base config from props or context
    const propsXScale = xyChartProps.xScale as { type?: string; domain?: unknown } | undefined;
    const scaleType = propsXScale?.type ?? xScaleType;
    const propsConfig = typeof xyChartProps.xScale === 'object' ? { ...xyChartProps.xScale } : {};

    // Get domain: props domain takes precedence, then use normalizeDomain for zoom/data
    // normalizeDomain uses xScaleType to determine the correct normalization strategy
    const domain = propsXScale?.domain ?? normalizeDomain(xScaleType, zoomDomain, chartData, xDataKey);
    const isZoomed = zoomDomain !== null;

    if (scaleType === 'band') {
      return {
        type: 'band' as const,
        paddingInner: 0.1,
        paddingOuter: 0.2,
        ...propsConfig,
        domain,
      };
    }

    return {
      type: scaleType as 'time' | 'linear',
      ...propsConfig,
      domain,
      ...(isZoomed && { nice: false, zero: false }),
    };
  }, [xScaleType, zoomDomain, chartData, xDataKey, xyChartProps.xScale, hasData]);

  // Extract xScale and yScale from props to prevent passing them directly (we use computed configs)
  const { xScale: _xScale, yScale: propsYScale, ...restProps } = xyChartProps;

  // if user provides a yScale, use it, otherwise use the context
  // context yScale handles auto-scaling with zoom
  const yScaleConfig = propsYScale ?? contextYScaleConfig;

  // Priority: loading state > empty state > chart content
  if (isLoading) {
    return (
      <div
        ref={containerRef}
        className={clsx(styles.container, styles.zoomDisabled)}
        data-visx-xychart-container
        data-chart-id={containerId}
        {...pointerEventHandlers}
      >
        <SkeletonChart />
      </div>
    );
  }

  if (!hasData) {
    return (
      <div
        ref={containerRef}
        className={clsx(styles.container, styles.zoomDisabled)}
        data-visx-xychart-container
        data-chart-id={containerId}
        {...pointerEventHandlers}
      >
        <EmptyChart title={emptyStateTitle} subtitle={emptyStateSubtitle} />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={clsx(styles.container, disableDragToZoom && styles.zoomDisabled)}
      data-visx-xychart-container
      data-chart-id={containerId}
      {...pointerEventHandlers}
    >
      <XYChart
        {...restProps}
        xScale={xScaleConfig as ComponentProps<typeof XYChart>['xScale']}
        yScale={yScaleConfig}
        theme={theme}
      >
        {/* Define clip path for series to use - constrains data to chart area */}
        <ChartClipPath clipPathId={chartClipPathId} margin={restProps.margin} />
        <DragToZoomHandler containerId={containerId} />
        {onDataPointHover && <HoverTrackingHandler />}
        {children}
      </XYChart>
      <ResetZoomButton />
    </div>
  );
}
